// src/hooks/useFileUpload.ts
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { fromArrayBuffer } from 'geotiff';
import { FileNode, ShapefileParts } from '../types/FileManagerTypes';
import { allowedExtensions } from '../views/FileManager/constants';
import axiosInstance from '../interceptor/axiosInstance';
import { ApiRoutes } from '../routes/routeConstants/apiRoutes';
import { FileTypes } from '../enums/fileTypes';
import { uploadFileInParts, MULTIPART_THRESHOLD_BYTES } from '../shared/utils/s3MultipartUpload';
import { isCancelError } from '../shared/utils/isCancelError';
import { UploadDestination } from '../enums/uploadDestination';
import { UploadPhase } from '../enums/uploadPhase';
import Notification from '../shared/components/Notification';
import { NotificationTypes } from '../enums/notificationTypes';

const normalizeFolderS3Key = (key?: string) => (key && key !== 'null' ? key : '');

/** Best-effort type for /s3/upload-complete when the batch is geospatial. */
export const inferUploadType = (files: { name: string }[]): FileTypes | undefined => {
  const exts = files.map((f) => f.name.split('.').pop()?.toLowerCase() ?? '');
  if (exts.some((e) => e === 'shp')) return FileTypes.SHAPEFILE;
  if (exts.some((e) => e === 'tif' || e === 'tiff')) return FileTypes.RASTER;
  if (exts.some((e) => e === 'geojson')) return FileTypes.GEOJSON;
  if (exts.some((e) => e === 'kml')) return FileTypes.KML;
  if (exts.some((e) => e === 'kmz')) return FileTypes.KMZ;
  return undefined;
};

export const useFileUpload = (
  currentFolderId: string,
  updateMapping: () => void,
  /** Full S3 path of the target folder (id chain), e.g. `root-1/folderA`. Files land at `{folderS3Key}/{fileName}`. */
  folderS3Key: string,
  /**
   * Called only after a successful reupload finishes end-to-end
   * (S3 PUT + /s3/upload-complete + updateMapping). Not fired on progress-100%
   * alone — upload-complete may still be in flight then.
   */
  onUploadComplete?: () => void,
) => {
  const [selectedFiles, setSelectedFiles] = useState<FileList | File[] | null>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number> | null>(null);
  const [reuploadProgress, setReuploadProgress] = useState<Record<string, number> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const uploadPhaseRef = useRef<UploadPhase>(UploadPhase.IDLE);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadDraggedFiles, setUploadDraggedFiles] = useState(false);

  const beginUpload = useCallback((controller: AbortController) => {
    abortControllerRef.current = controller;
    uploadPhaseRef.current = UploadPhase.UPLOADING;
    setIsUploading(true);
  }, []);

  // Takes the run's controller so a cancelled run settling late can't release
  // an upload that has already started. Omitted to release unconditionally.
  const endUpload = useCallback((controller?: AbortController) => {
    if (controller && abortControllerRef.current !== controller) return;
    abortControllerRef.current = null;
    uploadPhaseRef.current = UploadPhase.IDLE;
    setIsUploading(false);
  }, []);

  const markFinalizing = useCallback((controller: AbortController) => {
    if (abortControllerRef.current !== controller) return;
    uploadPhaseRef.current = UploadPhase.FINALIZING;
  }, []);

  const updateFileProgress = (fileId: string, percentCompleted: number) => {
    setReuploadProgress((prev) => ({ ...(prev ?? {}), [fileId]: percentCompleted }));
  };

  useEffect(() => {
    if (uploadDraggedFiles && selectedFiles && selectedFiles.length > 0) {
      handleFileUpload();
      setSelectedFiles(null);
      setUploadDraggedFiles(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiles, uploadDraggedFiles]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, isDragged = false) => {
    const files = event.target.files;
    setSelectedFiles(files);
    setUploadDraggedFiles(isDragged);
  };

  const reportUploadProgress = (fileName: string, percentCompleted: number) => {
    setUploadProgress((prev) => ({ ...(prev ?? {}), [fileName]: percentCompleted }));
  };

  const uploadFileToPresignedUrl = async (
    file: File,
    presignedUrl: string,
    signal?: AbortSignal,
    /** Where to report progress — the upload and reupload flows track separate state. */
    onProgress: (fileName: string, percent: number) => void = reportUploadProgress,
  ): Promise<boolean> => {
    // Bare axios (NOT axiosInstance): the presigned S3 URL must not carry the
    // app's Authorization header/credentials or it breaks the SigV4 signature
    // (S3 returns 403). Same reasoning as s3MultipartUpload.ts.
    const response = await axios.put(presignedUrl, file, {
      signal,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
      onUploadProgress: (progressEvent) => {
        // A request that already finished sending can still fire a trailing
        // progress event after abort() — ignore it so a cancelled upload
        // can't write state back and reopen the progress modal.
        if (signal?.aborted || !progressEvent.total) return;
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(file.name, percentCompleted);
      },
    });
    return response.status === 200;
  };

  const completeS3Upload = async (files: File[], destinationType: UploadDestination) => {
    const normalizedKey = normalizeFolderS3Key(folderS3Key);
    const fileNames = files.map((file) => file.name);
    const type = inferUploadType(files);

    const payload: Record<string, unknown> = {
      currentFolderId,
      // Existing folder upload: leaf folder name is the current folder, not a new uuid subfolder.
      s3UniqueFolderName: currentFolderId,
      fileNames,
      folderS3Key: normalizedKey,
      reupload: destinationType === UploadDestination.REUPLOAD,
      destinationType,
      ...(type && { type }),
    };

    const response = await axiosInstance.post(ApiRoutes.S3_UPLOAD_COMPLETE, payload);
    return Array.isArray(response.data?.files) && response.data.files.length > 0;
  };

  /** The S3 key a file lands at — files go directly in the current folder. */
  const keyFor = (file: File) => `${normalizeFolderS3Key(folderS3Key)}/${file.name}`;

  /** Backend validation failures (400) carry a user-facing message; show it verbatim. */
  const uploadErrorMessage = (error: unknown, fallback: string) => {
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      return error.response.data?.message ?? fallback;
    }
    return fallback;
  };

  const handleFileUpload = async (
    reuploadList?: FileNode[],
    onSuccess?: () => void,
    // Every File Manager path uploads into an already-resolved folder, so
    // EXISTING is the correct default — including drag-and-drop, which calls
    // this with no arguments.
    destinationType: UploadDestination = reuploadList ? UploadDestination.REUPLOAD : UploadDestination.EXISTING,
  ) => {
    if (!selectedFiles || selectedFiles.length === 0) {
      Notification({ message: 'No files selected.', type: NotificationTypes.ERROR });
      return;
    }
    // Destination comes from the resolved root; uploading before it lands would
    // key objects under a folder that does not exist.
    if (!currentFolderId || !normalizeFolderS3Key(folderS3Key)) {
      Notification({ message: 'Folder is still loading. Please try again.', type: NotificationTypes.ERROR });
      return;
    }
    // Logic matching original fileUpload.ts...
    const maxFileSize = 5 * 2 ** 30; // 5 GiB — matches the highest per-type Dragger limit (TIF/TIFF)
    const maxCompressedFileSize = 1 * 2 ** 30; // 1 GiB
    const fileList: File[] = Array.from(selectedFiles);

    function isCompressedFileExtension(ext: string): boolean {
      return ['zip', 'rar', '7z', 'gz', 'tar'].includes(ext);
    }

    const checkTifAndTifw = async (fileList: File[]) => {
      const tifFiles = fileList.filter((file) => file.name.match(/\.(tif|tiff)$/i));
      const worldFiles = fileList.filter((file) => file.name.match(/\.(tfw|tifw|tfwx)$/i));
      const worldFileNames = new Set(worldFiles.map((file) => file.name.substring(0, file.name.lastIndexOf('.')).toLowerCase()));

      async function isGeoTIFF(file: File) {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const tiff = await fromArrayBuffer(arrayBuffer);
          const image = await tiff.getImage();
          const geoKeys = image.getGeoKeys();
          return geoKeys && Object.keys(geoKeys).length > 0;
        } catch (error) {
          return false;
        }
      }

      for (let file of tifFiles) {
        const baseName = file.name.substring(0, file.name.lastIndexOf('.')).toLowerCase();
        const hasWorldFile = worldFileNames.has(baseName);
        const geoTIFF = await isGeoTIFF(file);
        if (!geoTIFF && !hasWorldFile) return true;
      }
      return false;
    };

    if (await checkTifAndTifw(fileList)) {
      Notification({ message: 'Each non-GeoTIFF TIF/TIFF file must have a corresponding .tfw or .tifw file.', type: NotificationTypes.ERROR });
      return;
    }

    function isShapefileExtension(ext: string): ext is keyof ShapefileParts {
      return ['shp', 'shx', 'dbf', 'prj'].includes(ext);
    }

    let shapefileParts: ShapefileParts = { shp: false, shx: false, dbf: false, prj: false };

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!ext) continue;

      const currentMaxFileSize = isCompressedFileExtension(ext) ? maxCompressedFileSize : maxFileSize;
      if (file.size > currentMaxFileSize) {
        Notification({ message: `The file ${file.name} exceeds the maximum size limit.`, type: NotificationTypes.ERROR });
        return;
      }
      if (!allowedExtensions.has(ext)) {
        Notification({ message: `Invalid file type: ${ext}.`, type: NotificationTypes.ERROR });
        return;
      }
      if (isShapefileExtension(ext)) shapefileParts[ext] = true;
    }

    const allShapefilePartsSelected = Object.keys(shapefileParts).every((ext) => shapefileParts[ext as keyof ShapefileParts]);
    if (Object.values(shapefileParts).some((val) => val) && !allShapefilePartsSelected) {
      Notification({ message: 'Please select all required Shapefile parts (.shp, .shx, .dbf, .prj).', type: NotificationTypes.ERROR });
      return;
    }

    // Re-entrancy guard: a rapid double-click on Add/Upload while a batch is
    // already running must not start a second, concurrent one.
    if (uploadPhaseRef.current !== UploadPhase.IDLE) return;

    const controller = new AbortController();
    beginUpload(controller);

    if (reuploadList) {
      // Direct-to-S3 reupload. Three steps, and the ordering is the point:
      //   1. /s3/presigned-url validates the whole set (components, prefix, file
      //      type vs the dataset being replaced) BEFORE issuing any URL, so an
      //      invalid reupload never uploads a byte.
      //   2. PUT each file straight to S3 (multipart for large ones).
      //   3. /s3/upload-complete re-checks what actually landed, reads the .shp
      //      header to compare geometry, and only then retires the old version
      //      and queues re-ingest. The old dataset stays live until this
      //      succeeds — so a rejection or an abandoned upload costs nothing.
      // The backend queues ingestion itself, which is why there's no longer a
      // /filemanager/process-shape-file follow-up call.
      try {
        const files: File[] = Array.from(selectedFiles);

        // Seed progress so the UI lists each file immediately.
        setReuploadProgress(
          files.reduce<Record<string, number>>((acc, file) => {
            acc[file.name] = 0;
            return acc;
          }, {}),
        );

        const keys = files.map(keyFor);
        // Presign every file in ONE call — that's what lets the backend judge the
        // set as a unit. Large files ignore the returned URL and go multipart,
        // but still take part in the validation.
        const { data } = await axiosInstance.post(
          ApiRoutes.PRESIGNED_URL,
          { fileNames: keys, reupload: true, targetFolderId: currentFolderId },
          { signal: controller.signal },
        );
        const urlByKey = new Map<string, string>(
          (data.urls ?? []).map((u: { fileName: string; presignedUrl: string }) => [u.fileName, u.presignedUrl]),
        );

        const reuploadOne = async (file: File, key: string): Promise<boolean> => {
          if (controller.signal.aborted) return false;
          if (file.size >= MULTIPART_THRESHOLD_BYTES) {
            await uploadFileInParts({
              file,
              key,
              onProgress: (percent) => {
                if (controller.signal.aborted) return;
                updateFileProgress(file.name, percent);
              },
              signal: controller.signal,
            });
            if (controller.signal.aborted) return false;
            return true;
          }
          const presignedUrl = urlByKey.get(key);
          if (!presignedUrl) return false;
          const ok = await uploadFileToPresignedUrl(file, presignedUrl, controller.signal, updateFileProgress);
          if (controller.signal.aborted) return false;
          if (ok) updateFileProgress(file.name, 100);
          return ok;
        };

        const results = await Promise.all(files.map((file, i) => reuploadOne(file, keys[i])));
        if (controller.signal.aborted) {
          // Cancellation is user-initiated — cancelUpload() already reset
          // state and toasted; nothing further to do here.
          return;
        }
        if (!results.every(Boolean)) {
          setReuploadProgress(null);
          Notification({ message: 'One or more files failed to upload.', type: NotificationTypes.ERROR });
          return;
        }

        // Every file is at 100% in S3 — cancelling from here would orphan them.
        markFinalizing(controller);
        const completed = await completeS3Upload(files, destinationType);
        if (!completed) {
          setReuploadProgress(null);
          Notification({ message: 'Files uploaded to storage but failed to register.', type: NotificationTypes.ERROR });
          return;
        }

        // Await so callers that refresh dependent views (e.g. spatial preview)
        // see the post-complete file map, not a race with the listing fetch.
        await Promise.resolve(updateMapping());
        onUploadComplete?.();
        Notification({
          message: files.length === 1 ? `${files[0].name} reuploaded successfully.` : `${files.length} files reuploaded successfully.`,
          type: NotificationTypes.SUCCESS,
        });
        setTimeout(() => setReuploadProgress(null), 1000);
        setSelectedFiles(null);
        setUploadDraggedFiles(false);
        onSuccess?.();
      } catch (error) {
        if (!isCancelError(error)) {
          console.error('Error reuploading files:', error);
          // Surfaces the backend's validation message (file type / geometry
          // mismatch, missing components) rather than a generic failure.
          Notification({ message: uploadErrorMessage(error, 'Error reuploading files.'), type: NotificationTypes.ERROR });
          setReuploadProgress(null);
        }
      } finally {
        endUpload(controller);
      }
    } else {
      // Direct-to-S3: presigned URL (or multipart) per file, then /s3/upload-complete.
      try {
        const normalizedKey = normalizeFolderS3Key(folderS3Key);
        const files: File[] = Array.from(selectedFiles);

        // Seed progress so the UI shows each file immediately.
        setUploadProgress(
          files.reduce<Record<string, number>>((acc, file) => {
            acc[file.name] = 0;
            return acc;
          }, {}),
        );

        const uploadOne = async (file: File): Promise<boolean> => {
          // Files are placed directly in the current folder (no unique subfolder).
          const key = `${normalizedKey}/${file.name}`;
          try {
            if (controller.signal.aborted) return false;
            if (file.size >= MULTIPART_THRESHOLD_BYTES) {
              await uploadFileInParts({
                file,
                key,
                onProgress: (percent) => {
                  if (controller.signal.aborted) return;
                  setUploadProgress((prev) => ({ ...(prev ?? {}), [file.name]: percent }));
                },
                signal: controller.signal,
              });
              if (controller.signal.aborted) return false;
              return true;
            }

            const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
            const { data } = await axiosInstance.post(ApiRoutes.PRESIGNED_URL, { fileName: key, fileType: ext }, { signal: controller.signal });
            if (controller.signal.aborted) return false;
            const ok = await uploadFileToPresignedUrl(file, data.presignedUrl.presignedUrl, controller.signal);
            if (controller.signal.aborted) return false;
            if (ok) {
              setUploadProgress((prev) => ({ ...(prev ?? {}), [file.name]: 100 }));
            }
            return ok;
          } catch (error) {
            if (!isCancelError(error)) {
              console.error(`Error uploading ${file.name}:`, error);
              Notification({ message: uploadErrorMessage(error, `Upload failed for ${file.name}.`), type: NotificationTypes.ERROR });
            }
            return false;
          }
        };

        const results = await Promise.all(files.map(uploadOne));

        // Cancellation is user-initiated, not a failure — no batch error toast.
        // uploadOne swallows its own errors (including the abort), so this
        // check is what tells a real failure apart from a cancel here.
        if (controller.signal.aborted) {
          return;
        }

        if (!results.every(Boolean)) {
          setUploadProgress(null);
          Notification({ message: 'One or more files failed to upload.', type: NotificationTypes.ERROR });
          return;
        }

        // Every file is at 100% in S3 — cancelling from here would orphan them.
        markFinalizing(controller);
        const completed = await completeS3Upload(files, destinationType);
        if (completed) {
          updateMapping();
          Notification({
            message: files.length === 1 ? `${files[0].name} uploaded successfully.` : `${files.length} files uploaded successfully.`,
            type: NotificationTypes.SUCCESS,
          });
          setTimeout(() => setUploadProgress(null), 1000);
          setSelectedFiles(null);
          setUploadDraggedFiles(false);
          onSuccess?.();
        } else {
          setUploadProgress(null);
          Notification({ message: 'Files uploaded to storage but failed to register.', type: NotificationTypes.ERROR });
        }
      } catch (error) {
        if (!isCancelError(error)) {
          console.error('Error in S3 file upload:', error);
          Notification({ message: 'Error uploading files.', type: NotificationTypes.ERROR });
          setUploadProgress(null);
        }
      } finally {
        endUpload(controller);
      }
    }
  };

  // Cancelling aborts the in-flight S3 PUTs. Nothing needs rolling back: a
  // reupload only touches the database at /s3/upload-complete, which runs after
  // every file has landed — so a cancelled reupload leaves the previous dataset
  // active and untouched. (The old multer flow wrote rows per file as they
  // arrived, which is why it needed /filemanager/cancel-reupload to undo them.)
  const cancelUpload = useCallback(() => {
    // Too late to cancel: the bytes are already in S3 and registration is in
    // flight, so honouring this would leave orphaned objects behind.
    if (uploadPhaseRef.current === UploadPhase.FINALIZING) return;
    abortControllerRef.current?.abort();
    setUploadProgress(null);
    setReuploadProgress(null);
    endUpload();
    Notification({ message: 'Upload canceled.', type: NotificationTypes.SUCCESS });
  }, [endUpload]);

  return {
    selectedFiles,
    setSelectedFiles,
    uploadProgress,
    reuploadProgress,
    isUploading,
    cancelUpload,
    handleFileChange,
    handleFileUpload,
  };
};
